import { useEffect, useRef } from "react";
import { AppLoadingState } from "../shared/AppLoadingState.tsx";
import { SPACE_MGMT_COPY } from "../space/spaceMgmtCopy.ts";
import { commonCopy } from "./copy.ts";
import { showActionSheet } from "./showActionSheet.ts";
import { spaceCopy } from "./spaceCopy.ts";
import { TextPromptDialog } from "./TextPromptDialog.tsx";
import type { useSpaceSheet } from "./useSpaceSheet.ts";
import "./spaceCreationLoading.css";

type Props = { sheet: ReturnType<typeof useSpaceSheet> };

export const SpaceSheetDialogs = ({ sheet: s }: Props) => {
  const submitRename = () => {
    const trimmed = s.promptValue.trim();
    if (!trimmed) return;
    void s.mgmt.rename(trimmed);
    s.resetSubDialog();
  };
  const submitInvite = async () => {
    const trimmed = s.promptValue.trim();
    if (!trimmed) return;
    const sent = await s.mgmt.invite(trimmed);
    s.resetSubDialog();
    showActionSheet(sent ? spaceCopy.inviteSent : SPACE_MGMT_COPY.inviteUserNotFound, [
      { text: commonCopy.ok, style: "cancel" },
    ]);
  };

  return (
    <>
      <TextPromptDialog
        visible={s.subDialog === "create"}
        title={spaceCopy.createSpacePrompt}
        confirmLabel={spaceCopy.createSpaceConfirm}
        value={s.promptValue}
        disabled={s.creatingSpace}
        editable={!s.creatingSpace}
        onChange={s.setPromptValue}
        onCancel={s.resetSubDialog}
        onSubmit={s.handleCreateSubmit}
      />
      <TextPromptDialog
        visible={s.subDialog === "rename"}
        title={spaceCopy.renamePrompt}
        confirmLabel={spaceCopy.renameConfirm}
        value={s.promptValue}
        onChange={s.setPromptValue}
        onCancel={s.resetSubDialog}
        onSubmitText={() => submitRename()}
        onSubmit={submitRename}
      />
      <TextPromptDialog
        visible={s.subDialog === "invite"}
        title={spaceCopy.invitePrompt}
        confirmLabel={spaceCopy.inviteConfirm}
        value={s.promptValue}
        keyboardType="email-address"
        onChange={s.setPromptValue}
        onCancel={s.resetSubDialog}
        onSubmitText={async () => submitInvite()}
        onSubmit={() => void submitInvite()}
      />
      <SpaceCreationLoading visible={s.creatingSpace} />
    </>
  );
};

const SpaceCreationLoading = ({ visible }: { visible: boolean }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (visible && !dialog?.open) dialog?.showModal();
    else if (!visible && dialog?.open) dialog.close();
  }, [visible]);
  return (
    <dialog
      ref={dialogRef}
      className="space-creation-loading"
      aria-label={spaceCopy.createSpacePrompt}
      onCancel={(event) => event.preventDefault()}
    >
      <AppLoadingState />
    </dialog>
  );
};
